import { useEffect, useRef, useCallback, useState } from 'react';
import DirectVideoPlayer from './DirectVideoPlayer';
import { apiFetch } from '../lib/api';

interface VideoPlayerProps {
  videoId: string;
  autoplay?: boolean;
  startAt?: number;
  onProgress?: (position: number, duration: number) => void;
  onVideoEnd?: () => void;
  playRequestToken?: string | null;
  preferDirectPlayback?: boolean;
}

interface StreamOption {
  itag?: number;
  url: string;
  quality: string;
  mimeType: string;
  width?: number;
  height?: number;
  hasAudio: boolean;
  hasVideo: boolean;
  bitrate?: number;
  audioTrackId?: string;
  audioTrackName?: string;
  audioLanguage?: string;
  audioIsDefault?: boolean;
}

type PlayerMode = 'loading' | 'direct' | 'iframe';

interface StreamResponse {
  streams: StreamOption[];
  embeddable?: boolean;
  nTransform?: string;
}

const streamFetchCache = new Map<string, Promise<StreamResponse>>();

function applyNTransform(data: StreamResponse): StreamResponse {
  if (!data.nTransform || !data.streams?.length) return data;
  try {
    // eslint-disable-next-line no-new-func
    const transformFn = new Function('return ' + data.nTransform)();
    const transformed = data.streams.map((s) => {
      try {
        const u = new URL(s.url);
        const n = u.searchParams.get('n');
        if (n) {
          u.searchParams.set('n', transformFn(n));
          return { ...s, url: u.toString() };
        }
      } catch { /* keep original */ }
      return s;
    });
    return { ...data, streams: transformed };
  } catch (e) {
    console.warn('[VideoPlayer] n-transform failed:', e);
    return data;
  }
}

function fetchStreamData(videoId: string, preferDirectPlayback: boolean, forceRefresh = false): Promise<StreamResponse> {
  const cacheKey = `${videoId}:${preferDirectPlayback ? 'direct' : 'embed'}`;
  if (forceRefresh) streamFetchCache.delete(cacheKey);
  const cached = streamFetchCache.get(cacheKey);
  if (cached) return cached;

  let promise: Promise<StreamResponse>;
  promise = apiFetch<StreamResponse>(
    `/api/stream?v=${encodeURIComponent(videoId)}${preferDirectPlayback ? '&direct=1' : ''}${forceRefresh ? `&refresh=${Date.now()}` : ''}`,
    { cache: 'no-store' },
  )
    .then(applyNTransform)
    .catch(err => {
      throw err;
    })
    .finally(() => {
      if (streamFetchCache.get(cacheKey) === promise) {
        streamFetchCache.delete(cacheKey);
      }
    });

  streamFetchCache.set(cacheKey, promise);
  return promise;
}

export default function VideoPlayer({
  videoId,
  autoplay = false,
  startAt = 0,
  onProgress,
  onVideoEnd,
  playRequestToken,
  preferDirectPlayback = false,
}: VideoPlayerProps) {
  const [mode, setMode] = useState<PlayerMode>('loading');
  const [streams, setStreams] = useState<StreamOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const directRetryRef = useRef(false);

  const pickBestDirectStreams = useCallback((allStreams: StreamOption[]): StreamOption[] => {
    const itag18 = allStreams.find((stream) =>
      Number(stream.itag) === 18 &&
      stream.hasVideo &&
      stream.hasAudio &&
      stream.mimeType.toLowerCase().startsWith('video/mp4'),
    );
    return itag18 ? [itag18] : [];
  }, []);

  const handleEmbedBlocked = useCallback(() => {
    console.warn('[VideoPlayer] Embed blocked at runtime, switching to direct player');
    setMode('loading');
    (async () => {
      try {
        const data = await fetchStreamData(videoId, true);
        const selected = pickBestDirectStreams(data.streams || []);
        if (selected.length) {
          setStreams(selected);
          setMode('direct');
          return;
        }
      } catch (err) {
        console.warn('[VideoPlayer] Stream fetch failed:', err);
      }
      setError('This video cannot be played (embedding disabled and no direct stream available)');
    })();
  }, [videoId, pickBestDirectStreams]);

  const handleDirectPlaybackError = useCallback(() => {
    if (directRetryRef.current) {
      setError('This video stream expired or could not be loaded. Please try the video again.');
      return;
    }

    directRetryRef.current = true;
    setMode('loading');
    fetchStreamData(videoId, true, true)
      .then((data) => {
        const selected = pickBestDirectStreams(data.streams || []);
        if (!selected.length) throw new Error('No direct stream available');
        setStreams(selected);
        setMode('direct');
      })
      .catch(() => {
        setError('This video stream expired or could not be loaded. Please try the video again.');
      });
  }, [videoId, pickBestDirectStreams]);

  useEffect(() => {
    let cancelled = false;
    setStreams([]);
    setError(null);
    setMode('loading');
    directRetryRef.current = false;

    fetchStreamData(videoId, preferDirectPlayback)
      .then(data => {
        if (cancelled) return;
        if (data.embeddable !== false && !preferDirectPlayback) {
          setMode('iframe');
        } else {
          const selected = pickBestDirectStreams(data.streams || []);
          if (selected.length) {
            setStreams(selected);
            setMode('direct');
          } else {
            setError('This video cannot be played (embedding disabled and no direct stream available)');
          }
        }
      })
      .catch(() => {
        if (cancelled) return;
        setMode('iframe');
      });

    return () => { cancelled = true; };
  }, [videoId, preferDirectPlayback, pickBestDirectStreams]);

  if (error) {
    return (
      <div className="player-wrapper">
        <div className="player-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', textAlign: 'center', padding: 20 }}>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (mode === 'loading') {
    return (
      <div className="player-wrapper">
        <div className="player-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            width: 40,
            height: 40,
            border: '3px solid rgba(255,255,255,0.15)',
            borderTopColor: '#fff',
            borderRadius: '50%',
            animation: 'dp-spin 0.8s linear infinite',
          }} />
        </div>
      </div>
    );
  }

  if (mode === 'direct') {
    return (
      <DirectVideoPlayer
        videoId={videoId}
        streams={streams}
        autoplay={autoplay || !!playRequestToken}
        startAt={startAt}
        onProgress={onProgress}
        onVideoEnd={onVideoEnd}
        onPlaybackError={handleDirectPlaybackError}
      />
    );
  }

  return (
    <EmbedVideoPlayer
      videoId={videoId}
      autoplay={autoplay}
      startAt={startAt}
      onProgress={onProgress}
      onVideoEnd={onVideoEnd}
      playRequestToken={playRequestToken}
      onEmbedBlocked={handleEmbedBlocked}
    />
  );
}

// ── YouTube IFrame fallback (existing implementation) ──

interface YTPlayer {
  destroy(): void;
  getCurrentTime(): number;
  getDuration(): number;
  getVideoLoadedFraction(): number;
  seekTo(seconds: number, allowSeekAhead?: boolean): void;
  playVideo(): void;
  pauseVideo(): void;
  loadVideoById(options: { videoId: string; startSeconds?: number }): void;
  cueVideoById(options: { videoId: string; startSeconds?: number }): void;
  getVolume(): number;
  setVolume(volume: number): void;
  isMuted(): boolean;
  mute(): void;
  unMute(): void;
}

interface YTPlayerEvent {
  data: number;
}

interface YTPlayerErrorEvent {
  data: number;
}

interface YTPlayerConstructor {
  new (elementId: string, options: {
    videoId: string;
    width?: string | number;
    height?: string | number;
    playerVars?: Record<string, number | string>;
    events?: {
      onReady?: () => void;
      onStateChange?: (event: YTPlayerEvent) => void;
      onError?: (event: YTPlayerErrorEvent) => void;
    };
  }): YTPlayer;
}

interface YTNamespace {
  Player: YTPlayerConstructor;
  PlayerState: {
    ENDED: number;
    PLAYING: number;
    PAUSED: number;
  };
}

declare global {
  interface Window {
    YT: YTNamespace;
    onYouTubeIframeAPIReady: () => void;
  }
}

let apiLoaded = false;
let apiLoading = false;
const apiReadyCallbacks: (() => void)[] = [];

function loadYouTubeAPI(): Promise<void> {
  return new Promise((resolve) => {
    if (apiLoaded) {
      resolve();
      return;
    }

    apiReadyCallbacks.push(resolve);

    if (apiLoading) return;
    apiLoading = true;

    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
      apiLoaded = true;
      apiReadyCallbacks.forEach(cb => cb());
      apiReadyCallbacks.length = 0;
    };
  });
}

interface EmbedVideoPlayerProps {
  videoId: string;
  autoplay?: boolean;
  startAt?: number;
  onProgress?: (position: number, duration: number) => void;
  onVideoEnd?: () => void;
  playRequestToken?: string | null;
  onEmbedBlocked?: () => void;
}

function EmbedVideoPlayer({
  videoId,
  autoplay = false,
  startAt = 0,
  onProgress,
  onVideoEnd,
  playRequestToken,
  onEmbedBlocked,
}: EmbedVideoPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YTPlayer | null>(null);
  const progressIntervalRef = useRef<number | null>(null);
  const playAttemptTimeoutRef = useRef<number | null>(null);
  const playerElementIdRef = useRef(`yt-player-${Math.random().toString(36).slice(2)}`);
  const onProgressRef = useRef(onProgress);
  const onVideoEndRef = useRef(onVideoEnd);
  const isPlayingRef = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const lastLoadRef = useRef<{ videoId: string; startAt: number; autoplay: boolean; playRequestToken?: string | null } | null>(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [needsUserPlay, setNeedsUserPlay] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [volume, setVolume] = useState(100);
  const [isMuted, setIsMuted] = useState(false);
  const volumeBeforeMuteRef = useRef(100);
  const idleTimerRef = useRef<number | null>(null);
  const isScrubbing = useRef(false);
  const scrubBarRef = useRef<HTMLDivElement>(null);
  const embedBlockedNotifiedRef = useRef(false);

  useEffect(() => { onProgressRef.current = onProgress; }, [onProgress]);
  useEffect(() => { onVideoEndRef.current = onVideoEnd; }, [onVideoEnd]);

  const resetIdleTimer = useCallback(() => {
    setControlsVisible(true);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = window.setTimeout(() => {
      if (isPlayingRef.current && !isScrubbing.current) setControlsVisible(false);
    }, 10000);
  }, []);

  const handleVolumeChange = useCallback((newVolume: number) => {
    if (!playerRef.current) return;
    const clamped = Math.max(0, Math.min(100, Math.round(newVolume)));
    playerRef.current.setVolume(clamped);
    setVolume(clamped);
    if (clamped > 0 && playerRef.current.isMuted?.()) {
      playerRef.current.unMute();
      setIsMuted(false);
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (!playerRef.current) return;
    if (playerRef.current.isMuted?.()) {
      playerRef.current.unMute();
      setIsMuted(false);
      if (volume === 0) handleVolumeChange(volumeBeforeMuteRef.current || 50);
    } else {
      volumeBeforeMuteRef.current = volume;
      playerRef.current.mute();
      setIsMuted(true);
    }
  }, [volume, handleVolumeChange]);

  const startProgressTracking = useCallback(() => {
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    progressIntervalRef.current = window.setInterval(() => {
      if (playerRef.current && onProgressRef.current) {
        try {
          const position = playerRef.current.getCurrentTime?.() || 0;
          const duration = playerRef.current.getDuration?.() || 0;
          if (duration > 0) onProgressRef.current(position, duration);
        } catch { /* Player might not be ready */ }
      }
    }, 30000);
  }, []);

  const stopProgressTracking = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  const clearPlayAttemptTimeout = useCallback(() => {
    if (playAttemptTimeoutRef.current) {
      clearTimeout(playAttemptTimeoutRef.current);
      playAttemptTimeoutRef.current = null;
    }
  }, []);

  const attemptPlayback = useCallback(() => {
    if (!playerRef.current) return;
    setNeedsUserPlay(false);
    setIsPlaying(true);
    isPlayingRef.current = true;
    try {
      playerRef.current.playVideo();
    } catch {
      setIsPlaying(false);
      isPlayingRef.current = false;
      setNeedsUserPlay(true);
      return;
    }
    clearPlayAttemptTimeout();
    playAttemptTimeoutRef.current = window.setTimeout(() => {
      if (!isPlayingRef.current) setNeedsUserPlay(true);
    }, 1200);
  }, [clearPlayAttemptTimeout]);

  const pausePlayback = useCallback(() => {
    if (!playerRef.current) return;
    setIsPlaying(false);
    isPlayingRef.current = false;
    try {
      playerRef.current.pauseVideo();
    } catch {
      // ignore
    }
  }, []);

  const [scrubFraction, setScrubFraction] = useState<number | null>(null);

  const seekToFraction = useCallback((fraction: number) => {
    const clamped = Math.max(0, Math.min(1, fraction));
    setScrubFraction(clamped);
    if (!playerRef.current) return;
    const total = playerRef.current.getDuration?.() || 0;
    if (!total || !Number.isFinite(total)) return;
    playerRef.current.seekTo(clamped * total, true);
  }, []);

  const toggleFullscreen = useCallback(() => { setIsFullscreen(prev => !prev); }, []);

  useEffect(() => {
    if (!isFullscreen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsFullscreen(false);
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  useEffect(() => {
    if (isFullscreen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isFullscreen]);

  useEffect(() => {
    let mounted = true;
    embedBlockedNotifiedRef.current = false;

    async function initPlayer() {
      await loadYouTubeAPI();
      if (!mounted || !containerRef.current) return;
      if (!containerRef.current.innerHTML) {
        containerRef.current.innerHTML = `<div id="${playerElementIdRef.current}"></div>`;
      }
      playerRef.current = new window.YT.Player(playerElementIdRef.current, {
        videoId,
        width: '100%',
        height: '100%',
        playerVars: {
          rel: 0,
          fs: 0,
          controls: 0,
          disablekb: 1,
          iv_load_policy: 3,
          playsinline: 1,
          hl: 'en',
          autoplay: autoplay ? 1 : 0,
          start: Math.floor(startAt),
          origin: window.location.origin,
        },
        events: {
          onReady: () => {
            lastLoadRef.current = { videoId, startAt: Math.floor(startAt), autoplay, playRequestToken };
            setPlayerReady(true);
            if (onProgressRef.current) startProgressTracking();
            if (autoplay || playRequestToken) attemptPlayback();
          },
          onStateChange: (event: YTPlayerEvent) => {
            if (event.data === 0) {
              isPlayingRef.current = false;
              setIsPlaying(false);
              stopProgressTracking();
              if (onProgressRef.current && playerRef.current) {
                const duration = playerRef.current.getDuration?.() || 0;
                onProgressRef.current(duration, duration);
              }
              setIsFullscreen(false);
              onVideoEndRef.current?.();
            } else if (event.data === 1) {
              isPlayingRef.current = true;
              setIsPlaying(true);
              setNeedsUserPlay(false);
              startProgressTracking();
            } else if (event.data === 2) {
              isPlayingRef.current = false;
              setIsPlaying(false);
              if (onProgressRef.current && playerRef.current) {
                const position = playerRef.current.getCurrentTime?.() || 0;
                const duration = playerRef.current.getDuration?.() || 0;
                onProgressRef.current(position, duration);
              }
            }
          },
          onError: (event: YTPlayerErrorEvent) => {
            const code = event?.data;
            // 101/150: owner disabled embedding. 100: unavailable/removed/private.
            if ((code === 101 || code === 150 || code === 100) && !embedBlockedNotifiedRef.current) {
              embedBlockedNotifiedRef.current = true;
              onEmbedBlocked?.();
            }
          },
        },
      });
    }

    initPlayer();
    return () => {
      mounted = false;
      stopProgressTracking();
      clearPlayAttemptTimeout();
      if (playerRef.current) { playerRef.current.destroy(); playerRef.current = null; }
    };
  }, [attemptPlayback, clearPlayAttemptTimeout, startProgressTracking, stopProgressTracking, onEmbedBlocked]);

  useEffect(() => {
    if (!playerReady || !playerRef.current) return;
    const tick = window.setInterval(() => {
      if (!playerRef.current) return;
      try {
        const pos = playerRef.current.getCurrentTime?.() || 0;
        const total = playerRef.current.getDuration?.() || 0;
        const loadedFrac = playerRef.current.getVideoLoadedFraction?.() || 0;
        if (!isScrubbing.current) setCurrentTime(pos);
        setDuration(total);
        setBuffered(total > 0 ? total * loadedFrac : 0);
        setVolume(playerRef.current.getVolume?.() ?? 100);
        setIsMuted(playerRef.current.isMuted?.() ?? false);
      } catch {
        // ignore transient player states
      }
    }, 500);
    return () => clearInterval(tick);
  }, [playerReady]);

  useEffect(() => {
    if (!playerReady || !playerRef.current) return;
    const loadKey = { videoId, startAt: Math.floor(startAt), autoplay, playRequestToken };
    const lastLoad = lastLoadRef.current;
    if (lastLoad && lastLoad.videoId === loadKey.videoId && lastLoad.startAt === loadKey.startAt && lastLoad.autoplay === loadKey.autoplay && lastLoad.playRequestToken === loadKey.playRequestToken) return;
    lastLoadRef.current = loadKey;
    if (autoplay || playRequestToken) {
      playerRef.current.loadVideoById({ videoId, startSeconds: loadKey.startAt });
      attemptPlayback();
    } else {
      playerRef.current.cueVideoById({ videoId, startSeconds: loadKey.startAt });
      setNeedsUserPlay(true);
    }
  }, [playerReady, videoId, startAt, autoplay, playRequestToken, attemptPlayback]);

  const displayTime = scrubFraction !== null && duration > 0 ? scrubFraction * duration : currentTime;
  const progressPct = scrubFraction !== null ? scrubFraction * 100 : (duration > 0 ? Math.max(0, Math.min(100, (currentTime / duration) * 100)) : 0);
  const bufferedPct = duration > 0 ? Math.max(0, Math.min(100, (buffered / duration) * 100)) : 0;

  function fmt(seconds: number): string {
    const total = Math.max(0, Math.floor(seconds));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  const onScrubMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const track = e.currentTarget;
    const rect = track.getBoundingClientRect();
    const getFraction = (clientX: number) => Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));

    seekToFraction(getFraction(e.clientX));

    const onMove = (ev: MouseEvent) => seekToFraction(getFraction(ev.clientX));
    const onUp = (ev: MouseEvent) => {
      seekToFraction(getFraction(ev.clientX));
      setScrubFraction(null);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [seekToFraction]);

  useEffect(() => {
    const el = scrubBarRef.current;
    if (!el) return;

    const onTouchStart = (e: TouchEvent) => {
      const rect = el.getBoundingClientRect();
      const getFraction = (clientX: number) => Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));

      const firstTouch = e.touches[0];
      if (!firstTouch) return;
      e.preventDefault();
      isScrubbing.current = true;
      seekToFraction(getFraction(firstTouch.clientX));

      const onMove = (ev: TouchEvent) => {
        const t = ev.touches[0];
        if (!t) return;
        ev.preventDefault();
        seekToFraction(getFraction(t.clientX));
      };
      const onEnd = (ev: TouchEvent) => {
        const t = ev.changedTouches[0];
        if (t) seekToFraction(getFraction(t.clientX));
        isScrubbing.current = false;
        setScrubFraction(null);
        window.removeEventListener('touchmove', onMove);
        window.removeEventListener('touchend', onEnd);
        window.removeEventListener('touchcancel', onEnd);
      };

      window.addEventListener('touchmove', onMove, { passive: false });
      window.addEventListener('touchend', onEnd, { passive: true });
      window.addEventListener('touchcancel', onEnd, { passive: true });
    };

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    return () => el.removeEventListener('touchstart', onTouchStart);
  }, [seekToFraction]);

  const controlBtnStyle: React.CSSProperties = {
    width: 44,
    height: 44,
    borderRadius: 22,
    border: 'none',
    background: 'rgba(255,255,255,0.18)',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    lineHeight: 0,
    flexShrink: 0,
  };

  return (
    <div
      className={`player-wrapper ${isFullscreen ? 'player-fullscreen' : ''} ${!controlsVisible ? 'player-controls-hidden' : ''}`}
      onMouseMove={resetIdleTimer}
      onMouseDown={resetIdleTimer}
      onTouchStart={resetIdleTimer}
    >
      <div ref={containerRef} className="player-container" />
      <div
        className="player-interaction-overlay"
        onClick={() => resetIdleTimer()}
      />
      {needsUserPlay && (
        <button className="player-play-overlay" onClick={attemptPlayback} aria-label="Play video">
          <span className="player-play-icon">▶</span>
          <span className="player-play-text">Tap to play</span>
        </button>
      )}
      <div className="player-controls-bar" onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
        <button
          onClick={() => { isPlaying ? pausePlayback() : attemptPlayback(); resetIdleTimer(); }}
          aria-label={isPlaying ? 'Pause' : 'Play'}
          style={controlBtnStyle}
        >
          {isPlaying ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="14" y="3" width="5" height="18" rx="1" />
              <rect x="5" y="3" width="5" height="18" rx="1" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z" />
            </svg>
          )}
        </button>
        <div className="player-volume-group">
          <button
            onClick={() => { toggleMute(); resetIdleTimer(); }}
            aria-label={isMuted || volume === 0 ? 'Unmute' : 'Mute'}
            style={controlBtnStyle}
          >
            {isMuted || volume === 0 ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M16 9a5 5 0 0 1 .95 2.293" /><path d="M19.364 5.636a9 9 0 0 1 1.889 9.96" /><path d="m2 2 20 20" /><path d="m7 7-.587.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298V11" /><path d="M9.828 4.172A.686.686 0 0 1 11 4.657v.686" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z" /><path d="M16 9a5 5 0 0 1 0 6" /><path d="M19.364 18.364a9 9 0 0 0 0-12.728" />
              </svg>
            )}
          </button>
          <input
            type="range"
            min={0}
            max={100}
            value={isMuted ? 0 : volume}
            onChange={(e) => { handleVolumeChange(Number(e.target.value)); resetIdleTimer(); }}
            className="player-volume-slider"
            aria-label="Volume"
          />
        </div>
        <div style={{ color: '#fff', fontSize: 14, minWidth: 90, flexShrink: 0 }}>{fmt(displayTime)} / {fmt(duration)}</div>
        <div
          ref={scrubBarRef}
          onMouseDown={(e) => { isScrubbing.current = true; onScrubMouseDown(e); const up = () => { isScrubbing.current = false; window.removeEventListener('mouseup', up); }; window.addEventListener('mouseup', up); }}
          style={{
            flex: 1,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            touchAction: 'none',
            minWidth: 0,
          }}
          aria-label="Seek bar"
          role="slider"
          aria-valuemin={0}
          aria-valuemax={Math.max(0, Math.floor(duration))}
          aria-valuenow={Math.max(0, Math.floor(currentTime))}
        >
          <div style={{ position: 'relative', width: '100%', height: 6, background: 'rgba(255,255,255,0.25)', borderRadius: 999 }}>
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${bufferedPct}%`, background: 'rgba(255,255,255,0.35)', borderRadius: 999 }} />
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${progressPct}%`, background: '#ff0000', borderRadius: 999 }} />
          </div>
        </div>
        <button
          onClick={() => { toggleFullscreen(); resetIdleTimer(); }}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          style={controlBtnStyle}
        >
          {isFullscreen ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M8 3v3a2 2 0 0 1-2 2H3" /><path d="M21 8h-3a2 2 0 0 1-2-2V3" /><path d="M3 16h3a2 2 0 0 1 2 2v3" /><path d="M16 21v-3a2 2 0 0 1 2-2h3" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M8 3H5a2 2 0 0 0-2 2v3" /><path d="M21 8V5a2 2 0 0 0-2-2h-3" /><path d="M3 16v3a2 2 0 0 0 2 2h3" /><path d="M16 21h3a2 2 0 0 0 2-2v-3" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}
